import React, { useRef, useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Grid, Typography, Paper, Box } from "@mui/material";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  IconButton,
  TableFooter,
  TablePagination,
  Toolbar,
} from "@mui/material";
import { usePDF } from "react-to-pdf";
import * as XLSX from "xlsx";
import { StyledButton, StyledCancelButton } from "components/StyledComponents/styledComponents";
import { format, differenceInMonths, differenceInDays } from "date-fns";
import BusinessSummaryBar from "./businessSummaryBar.js";
import VisibilityIcon from "@mui/icons-material/Visibility";
import URLs from "constants/urls.js";
import Cookie from "js-cookie";
import ExpenseBreakdownDialog from "./expenseBreakdownDailogue.js";
import DmaPayoutsDialog from "./dmaPayoutsDialog.js";
import AdditionalPayoutsDialog from "./AdditionalPayoutsDialog.js";
import RecoveriesDialog from "./RecoveriesDailog.js";

const formatINR = (value) =>
  Number(value).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

export default function ExpenditureIncomeReport({
  data,
  isIncomeByMis = false,
  startDate,
  endDate,
  setAlert,
  selectedDsaId,
  onConnectorClick,
}) {
  const { toPDF, targetRef } = usePDF({
    filename: "Expenditure_Income_Report.pdf",
    options: {
      method: "save",
      resolution: 3,
      page: { margin: 10, format: "A4", orientation: "portrait" },
      canvas: { mimeType: "image/png", qualityRatio: 1 },
    },
  });
  const [breakdownDialogOpen, setBreakdownDialogOpen] = useState(false);
  const [breakdownData, setBreakdownData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedLedger, setSelectedLedger] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const jwt_token = Cookie.get("jwt_token");
  const [dmaPayoutDialogOpen, setDmaPayoutDialogOpen] = useState(false);
  const [dmaPayoutData, setDmaPayoutData] = useState([]);
  const [dmaPayoutLoading, setDmaPayoutLoading] = useState(false);
  const [dmaPayoutError, setDmaPayoutError] = useState(null);
  const [dialogType, setDialogType] = useState("expense");
  const [additionalPayoutDialogOpen, setAdditionalPayoutDialogOpen] = useState(false);
  const [recoveriesDialogOpen, setRecoveriesDialogOpen] = useState(false);

  useEffect(() => {
    setPage(0);
  }, [breakdownData]);

  const handleBreakdownClick = async (ledgerName, type) => {
    setDialogType(type);
    setSelectedLedger(ledgerName);
    setBreakdownDialogOpen(true);
    setLoading(true);
    setError(null);
    try {
      if (!jwt_token) throw new Error("Authentication token not found");
      const formattedStartDate = startDate || "";
      const formattedEndDate = endDate || "";
      let url = "";

      if (type === "income") {
        if (selectedDsaId && selectedDsaId !== 0) {
          url =
            `${URLs.dmabusinessbreakdownbusinesstypecode}` +
            `?startDate=${formattedStartDate}` +
            `&endDate=${formattedEndDate}` +
            `&dsaId=${selectedDsaId}` +
            `&businessTypeCode=${ledgerName}`;
        } else {
          url =
            `${URLs.khProductTypeBusinessBreakdown}` +
            `?startDate=${formattedStartDate}` +
            `&endDate=${formattedEndDate}` +
            `&businessCode=${ledgerName}`;
        }
      } else if (type === "misIncome") {
        if (selectedDsaId && selectedDsaId !== 0) {
          url =
            `${URLs.dmamisbusinessbreakdownbusinesstypecode}` +
            `?startDate=${formattedStartDate}` +
            `&endDate=${formattedEndDate}` +
            `&dsaId=${selectedDsaId}` +
            `&businessTypeCode=${ledgerName}`;
        } else {
          url =
            `${URLs.khmisproducttypebusinessbreakDown}` +
            `?startDate=${formattedStartDate}` +
            `&endDate=${formattedEndDate}` +
            `&businessCode=${ledgerName}`;
        }
      } else {
        url =
          `${URLs.byfurcationOfExpenses}` +
          `?ledgerName=${encodeURIComponent(ledgerName)}` +
          `&startDate=${formattedStartDate}` +
          `&endDate=${formattedEndDate}` +
          `&companyId=${selectedDsaId ?? 0}`;
      }

      const response = await fetch(url, {
        method: "GET",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${jwt_token}` },
      });
      if (!response.ok) {
        const text = await response.text();
        throw new Error(`API failed (${response.status}): ${text}`);
      }
      const data = await response.json();
      setBreakdownData(data || []);
    } catch (err) {
      console.error("Breakdown API error:", err);
      setError(err.message || "Failed to fetch breakdown data");
    } finally {
      setLoading(false);
    }
  };

  const handleCloseDialog = () => {
    setBreakdownDialogOpen(false);
    setBreakdownData([]);
    setError(null);
    setPage(0);
    setRowsPerPage(10);
  };

  const handleDmaPayoutsClick = async () => {
    setDmaPayoutDialogOpen(true);
    setDmaPayoutLoading(true);
    setDmaPayoutError(null);
    try {
      if (!jwt_token) throw new Error("Authentication token not found");
      const url =
        `${URLs.dmaPayoutBreakdown}` +
        `?startDate=${startDate || ""}` +
        `&endDate=${endDate || ""}` +
        `&companyId=${selectedDsaId ?? 0}`;
      const response = await fetch(url, {
        method: "GET",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${jwt_token}` },
      });
      if (!response.ok) {
        const text = await response.text();
        throw new Error(`API failed (${response.status}): ${text}`);
      }
      const data = await response.json();
      setDmaPayoutData(data || []);
    } catch (err) {
      console.error("DMA Payouts API error:", err);
      setDmaPayoutError(err.message || "Failed to fetch DMA Payouts data");
    } finally {
      setDmaPayoutLoading(false);
    }
  };

  const handleCloseDmaPayoutDialog = () => {
    setDmaPayoutDialogOpen(false);
    setDmaPayoutData([]);
    setDmaPayoutError(null);
  };

  const handleExcelExport = () => {
    const expSheet = data.expenseDetails.map((e, idx) => [idx + 1, e.particulars, e.amount]);
    const incSheet = data.businessBreakdown.map((i, idx) => [idx + 1, i.particulars, i.amount]);
    if (data.subtotalGrossRevenue)
      incSheet.push(["", data.subtotalGrossRevenue.particulars, data.subtotalGrossRevenue.amount]);
    if (data.debitNotes) incSheet.push(["", data.debitNotes.particulars, data.debitNotes.amount]);
    if (data.creditNotes)
      incSheet.push([
        "",
        data.creditNotes.particulars,
        `(${Math.abs(data.creditNotes.amount).toFixed(2)})`,
      ]);
    if (data.netAdjustedRevenue)
      incSheet.push(["", data.netAdjustedRevenue.particulars, data.netAdjustedRevenue.amount]);
    const maxRows = Math.max(expSheet.length, incSheet.length);
    while (expSheet.length < maxRows) expSheet.push(["", "", ""]);
    while (incSheet.length < maxRows) incSheet.push(["", "", ""]);
    const finalData = [["S.No", "Expenditure", "Amount", "S.No", "Income", "Amount"]];
    for (let i = 0; i < maxRows; i++) {
      finalData.push([...expSheet[i], ...incSheet[i]]);
    }
    const totalIncomeAmt =
      data.netAdjustedRevenue?.amount ??
      data.totalEstRev?.amount ??
      data.receivedRevenue?.amount ??
      0;
    finalData.push([
      "",
      "Total Expenses",
      data.totalExpenses?.amount ?? 0,
      "",
      "NET ADJUSTED REVENUE",
      totalIncomeAmt,
    ]);
    finalData.push([
      "",
      (data.estimatedNetProfit?.amount ?? 0) >= 0 ? "Profit" : "Loss",
      Math.abs(data.estimatedNetProfit?.amount ?? 0),
      "",
      "",
      "",
    ]);
    const ws = XLSX.utils.aoa_to_sheet(finalData);
    ws["!cols"] = [{ wch: 8 }, { wch: 35 }, { wch: 18 }, { wch: 8 }, { wch: 30 }, { wch: 18 }];
    const headerCells = ["A1", "B1", "C1", "D1", "E1", "F1"];
    headerCells.forEach((cell) => {
      if (ws[cell]) ws[cell].s = { font: { bold: true } };
    });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Report");
    XLSX.writeFile(wb, "Expenditure_Income_Report.xlsx");
  };

  // KH summary rows
  const khSummaryRows = [
    {
      key: "subtotalGrossRevenue",
      bold: true,
      topBorder: true,
      bg: "#f5f5f5",
      red: false,
      brackets: false,
      onIconClick: null,
    },
    {
      key: "debitNotes",
      bold: false,
      topBorder: false,
      bg: "transparent",
      red: false,
      brackets: false,
      onIconClick: () => setAdditionalPayoutDialogOpen(true),
    },
    {
      key: "creditNotes",
      bold: false,
      topBorder: false,
      bg: "transparent",
      red: true,
      brackets: true,
      onIconClick: () => setRecoveriesDialogOpen(true),
    },
    {
      key: "netAdjustedRevenue",
      bold: true,
      topBorder: true,
      bg: "#f5f5f5",
      red: false,
      brackets: false,
      onIconClick: null,
    },
    {
      key: "estimatedNetProfit",
      bold: true,
      topBorder: true,
      bg: "#f5f5f5",
      red: false,
      brackets: false,
      onIconClick: null,
    },
  ];

  // MIS summary rows
  const misSummaryRows = [
    {
      key: "receivedRevenue",
      label: "Subtotal Gross Revenue",
      bold: true,
      topBorder: true,
      bg: "#f5f5f5",
      red: false,
      brackets: false,
    },
    {
      key: "additionalPayout",
      label: "(+) Debit Notes (Additions)",
      bold: false,
      topBorder: false,
      bg: "transparent",
      red: false,
      brackets: false,
      onIconClick: () => setAdditionalPayoutDialogOpen(true),
    },
    {
      key: "recoveryAmounts",
      label: "(-) Credit Notes (Reductions)",
      bold: false,
      topBorder: false,
      bg: "transparent",
      red: true,
      brackets: true,
      onIconClick: () => setRecoveriesDialogOpen(true),
    },
    {
      key: "totalNetReceivedAmount",
      label: "NET ADJUSTED REVENUE",
      bold: true,
      topBorder: true,
      bg: "#f5f5f5",
      red: false,
      brackets: false,
    },
    {
      key: "estimatedNetProfit",
      label: null,
      bold: true,
      topBorder: true,
      bg: "#f5f5f5",
      red: false,
      brackets: false,
    },
  ];

  const cellStyle = {
    borderBottom: "1px solid #eee",
    display: "flex",
    alignItems: "center",
  };

  return (
    <Paper sx={{ p: 3 }} ref={targetRef} elevation={3}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" mb={2}>
        <Typography variant="h6" gutterBottom>
          {isIncomeByMis
            ? "Expenditure and Income Report A/C to (MIS)"
            : "Expenditure and Income A/C to Account Statement"}
          <br />
          <span
            style={{
              backgroundColor: "#F5F5F5",
              color: "black",
              fontWeight: 600,
              borderRadius: "10px",
              padding: "4px 12px",
              display: "inline-block",
              cursor: "pointer",
              border: "1px solid #ccc",
              transition: "background-color 0.3s ease",
            }}
          >
            From {format(new Date(startDate), "dd-MMM-yyyy")} &nbsp;to&nbsp;
            {format(new Date(endDate), "dd-MMM-yyyy")} &nbsp; (
            {(() => {
              const months = differenceInMonths(new Date(endDate), new Date(startDate));
              const days = differenceInDays(new Date(endDate), new Date(startDate)) % 30;
              if (months > 0 && days > 0)
                return `${months} month${months > 1 ? "s" : ""} ${days} day${days > 1 ? "s" : ""}`;
              else if (months > 0) return `${months} month${months > 1 ? "s" : ""}`;
              else return `${days} day${days > 1 ? "s" : ""}`;
            })()}
            )
          </span>
        </Typography>
        <Box sx={{ textAlign: "center", mb: 2 }}>
          <Box>
            <StyledButton variant="contained" sx={{ mr: 1 }} onClick={() => toPDF()}>
              Download PDF
            </StyledButton>
            <StyledCancelButton variant="outlined" onClick={handleExcelExport}>
              Download Excel
            </StyledCancelButton>
          </Box>
        </Box>
      </Box>

      {/* ─── NON-MIS TABLE ─── */}
      {!isIncomeByMis && (
        <>
          {/* Table Header */}
          <Grid container sx={{ border: "1px solid #ccc" }}>
            {/* Main headers */}
            <Grid container>
              <Grid
                item
                xs={6}
                sx={{
                  borderRight: "1px solid #ccc",
                  p: 1,
                  background: "#e0f7fa",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                  Expenditure
                </Typography>
              </Grid>
              <Grid
                item
                xs={6}
                sx={{
                  p: 1,
                  background: "#e0f7fa",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                  Income
                </Typography>
              </Grid>
            </Grid>

            {/* Sub headers */}
            <Grid
              item
              xs={1}
              sx={{
                borderRight: "1px solid #ccc",
                borderTop: "1px solid #ccc",
                p: 1,
                background: "#f5f5f5",
                textAlign: "center",
              }}
            >
              <Typography variant="subtitle2">
                <strong>S.No</strong>
              </Typography>
            </Grid>
            <Grid
              item
              xs={3}
              sx={{
                borderRight: "1px solid #ccc",
                borderTop: "1px solid #ccc",
                p: 1,
                background: "#f5f5f5",
              }}
            >
              <Typography variant="subtitle2">
                <strong>Particulars</strong>
              </Typography>
            </Grid>
            <Grid
              item
              xs={2}
              sx={{
                borderRight: "1px solid #ccc",
                borderTop: "1px solid #ccc",
                p: 1,
                background: "#f5f5f5",
                textAlign: "right",
              }}
            >
              <Typography variant="subtitle2">
                <strong>Amount</strong>
              </Typography>
            </Grid>
            <Grid
              item
              xs={1}
              sx={{
                borderRight: "1px solid #ccc",
                borderTop: "1px solid #ccc",
                p: 1,
                background: "#f5f5f5",
                textAlign: "center",
              }}
            >
              <Typography variant="subtitle2">
                <strong>S.No</strong>
              </Typography>
            </Grid>
            <Grid
              item
              xs={3}
              sx={{
                borderRight: "1px solid #ccc",
                borderTop: "1px solid #ccc",
                p: 1,
                background: "#f5f5f5",
              }}
            >
              <Typography variant="subtitle2">
                <strong>Particulars</strong>
              </Typography>
            </Grid>
            <Grid
              item
              xs={2}
              sx={{ borderTop: "1px solid #ccc", p: 1, background: "#f5f5f5", textAlign: "right" }}
            >
              <Typography variant="subtitle2">
                <strong>Amount</strong>
              </Typography>
            </Grid>
          </Grid>

          {/* Table Body */}
          {(() => {
            const expRows = data.expenseDetails || [];
            const incRows = data.businessBreakdown || [];
            const filteredKhRows = khSummaryRows.filter(({ key }) => data[key] != null);
            const maxLength = Math.max(expRows.length, incRows.length + filteredKhRows.length);

            return Array.from({ length: maxLength }).map((_, idx) => {
              let incomeSerialNo = "";
              let incomeParticulars = "";
              let incomeAmount = null;
              let incomeBold = false;
              let incomeRed = false;
              let incomeBg = "transparent";
              let incomeTopBorder = false;
              let incomeBrackets = false;
              let isRegularIncomeRow = false;
              let incomeOnIconClick = null;

              if (idx < incRows.length) {
                isRegularIncomeRow = true;
                incomeSerialNo = idx + 1;
                incomeParticulars = incRows[idx]?.particulars || "";
                incomeAmount = incRows[idx]?.amount;
              } else {
                const khIdx = idx - incRows.length;
                if (khIdx < filteredKhRows.length) {
                  const { key, bold, topBorder, bg, red, brackets, onIconClick } =
                    filteredKhRows[khIdx];
                  incomeParticulars = data[key]?.particulars || "";
                  incomeAmount = data[key]?.amount;
                  incomeBold = bold;
                  incomeRed = red;
                  incomeBg = bg;
                  incomeTopBorder = topBorder;
                  incomeBrackets = brackets;
                  incomeOnIconClick = onIconClick || null;
                }
              }

              return (
                <Grid
                  container
                  key={idx}
                  sx={{
                    borderLeft: "1px solid #ccc",
                    borderRight: "1px solid #ccc",
                    borderBottom: "1px solid #eee",
                    minHeight: "42px",
                  }}
                >
                  {/* Expense S.No */}
                  <Grid
                    item
                    xs={1}
                    sx={{
                      borderRight: "1px solid #ccc",
                      p: 1,
                      textAlign: "center",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Typography sx={{ fontSize: "14px" }}>{expRows[idx] ? idx + 1 : ""}</Typography>
                  </Grid>

                  {/* Expense Particulars */}
                  <Grid
                    item
                    xs={3}
                    sx={{
                      borderRight: "1px solid #ccc",
                      p: 1,
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "14px",
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                      }}
                    >
                      <span style={{ flex: 1, minWidth: 0, wordBreak: "break-word" }}>
                        {expRows[idx]?.particulars || ""}
                      </span>
                      {expRows[idx]?.particulars && (
                        <IconButton
                          size="small"
                          onClick={() => {
                            const p = expRows[idx].particulars.trim().toUpperCase();
                            if (p === "DMA PAYOUTS") handleDmaPayoutsClick();
                            else handleBreakdownClick(expRows[idx].particulars, "expense");
                          }}
                          sx={{
                            ml: 0.5,
                            flexShrink: 0,
                            color: "primary.main",
                            "&:hover": { backgroundColor: "rgba(255,152,0,0.1)" },
                          }}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Typography>
                  </Grid>

                  {/* Expense Amount */}
                  <Grid
                    item
                    xs={2}
                    sx={{
                      borderRight: "1px solid #ccc",
                      p: 1,
                      textAlign: "right",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-end",
                    }}
                  >
                    <Typography sx={{ fontSize: "14px", fontWeight: "bold" }}>
                      {expRows[idx]?.amount != null ? formatINR(expRows[idx].amount) : ""}
                    </Typography>
                  </Grid>

                  {/* Income S.No */}
                  <Grid
                    item
                    xs={1}
                    sx={{
                      borderRight: "1px solid #ccc",
                      p: 1,
                      textAlign: "center",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: incomeBg,
                      borderTop: incomeTopBorder ? "2px solid #aaa" : "none",
                    }}
                  >
                    <Typography sx={{ fontSize: "14px" }}>{incomeSerialNo}</Typography>
                  </Grid>

                  {/* Income Particulars */}
                  <Grid
                    item
                    xs={3}
                    sx={{
                      borderRight: "1px solid #ccc",
                      p: 1,
                      display: "flex",
                      alignItems: "center",
                      background: incomeBg,
                      borderTop: incomeTopBorder ? "2px solid #aaa" : "none",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: incomeBold ? "0.95rem" : "14px",
                        fontWeight: incomeBold ? "bold" : "normal",
                        color: incomeRed ? "red" : "inherit",
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                      }}
                    >
                      <span style={{ flex: 1, minWidth: 0, wordBreak: "break-word" }}>
                        {incomeParticulars}
                      </span>
                      {isRegularIncomeRow && incomeParticulars && (
                        <IconButton
                          size="small"
                          onClick={() => handleBreakdownClick(incomeParticulars, "income")}
                          sx={{
                            ml: 0.5,
                            flexShrink: 0,
                            color: "primary.main",
                            "&:hover": { backgroundColor: "rgba(255,152,0,0.1)" },
                          }}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      )}
                      {!isRegularIncomeRow && incomeOnIconClick && incomeParticulars && (
                        <IconButton
                          size="small"
                          onClick={incomeOnIconClick}
                          sx={{
                            ml: 0.5,
                            flexShrink: 0,
                            color: "secondary.main",
                            "&:hover": { backgroundColor: "rgba(255,152,0,0.1)" },
                          }}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Typography>
                  </Grid>

                  {/* Income Amount */}
                  <Grid
                    item
                    xs={2}
                    sx={{
                      p: 1,
                      textAlign: "right",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-end",
                      background: incomeBg,
                      borderTop: incomeTopBorder ? "2px solid #aaa" : "none",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: incomeBold ? "0.95rem" : "14px",
                        fontWeight: incomeBold ? "bold" : "normal",
                        color: incomeRed ? "red" : "inherit",
                      }}
                    >
                      {incomeAmount != null
                        ? incomeBrackets
                          ? `(${formatINR(Math.abs(incomeAmount))})`
                          : formatINR(incomeAmount)
                        : ""}
                    </Typography>
                  </Grid>
                </Grid>
              );
            });
          })()}

          {/* Footer: Total Income */}
          <Grid
            container
            sx={{ border: "1px solid #ccc", borderTop: "none", background: "#f5f5f5" }}
          >
            <Grid item xs={6} />
            <Grid item xs={1} sx={{ borderLeft: "1px solid #ccc", p: 1 }} />
            <Grid item xs={3} sx={{ p: 1, border: "1px solid #ccc", fontWeight: "bold" }}>
              Total Income
            </Grid>
            <Grid
              item
              xs={2}
              sx={{
                p: 1,
                border: "1px solid #ccc",
                borderRight: "0px",
                textAlign: "right",
                fontWeight: "bold",
              }}
            >
              {(data.netAdjustedRevenue?.amount ?? data.income) != null
                ? formatINR(data.netAdjustedRevenue?.amount ?? data.income)
                : ""}
            </Grid>
          </Grid>

          {/* DMA Payouts */}
          {data.dmaPayouts && (
            <Grid
              container
              sx={{ border: "1px solid #ccc", borderTop: "none", background: "#fff" }}
            >
              <Grid item xs={1} sx={{ borderRight: "1px solid #ccc", p: 1 }} />
              <Grid
                item
                xs={3}
                sx={{
                  borderRight: "1px solid #ccc",
                  p: 1,
                  fontSize: "0.9rem",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <span style={{ flex: 1, minWidth: 0, wordBreak: "break-word" }}>
                  {data.dmaPayouts.particulars}
                </span>
                <IconButton
                  size="small"
                  onClick={handleDmaPayoutsClick}
                  sx={{ ml: 1, flexShrink: 0, color: "primary.main" }}
                >
                  <VisibilityIcon fontSize="small" />
                </IconButton>
              </Grid>
              <Grid
                item
                xs={2}
                sx={{ borderRight: "1px solid #ccc", p: 1, fontSize: "0.9rem", textAlign: "right" }}
              >
                {formatINR(data.dmaPayouts.amount)}
              </Grid>
              <Grid item xs={6} />
            </Grid>
          )}

          {/* Connector Payouts */}
          {data.connectorPayouts && (
            <Grid
              container
              sx={{ border: "1px solid #ccc", borderTop: "none", background: "#fff" }}
            >
              <Grid item xs={1} sx={{ borderRight: "1px solid #ccc", p: 1 }} />
              <Grid
                item
                xs={3}
                sx={{
                  borderRight: "1px solid #ccc",
                  p: 1,
                  fontSize: "0.9rem",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <span style={{ flex: 1, minWidth: 0, wordBreak: "break-word" }}>
                  {data.connectorPayouts.particulars}
                </span>
                <IconButton
                  size="small"
                  onClick={onConnectorClick}
                  sx={{ ml: 1, flexShrink: 0, color: "primary.main" }}
                >
                  <VisibilityIcon fontSize="small" />
                </IconButton>
              </Grid>
              <Grid
                item
                xs={2}
                sx={{ borderRight: "1px solid #ccc", p: 1, fontSize: "0.9rem", textAlign: "right" }}
              >
                {formatINR(data.connectorPayouts.amount)}
              </Grid>
              <Grid item xs={6} />
            </Grid>
          )}

          {/* Total Expenses */}
          {data.totalExpenses && (
            <Grid
              container
              sx={{
                border: "1px solid #ccc",
                borderTop: "2px solid #F97316",
                background: "#f5f5f5",
              }}
            >
              <Grid item xs={1} sx={{ borderRight: "1px solid #ccc", p: 1 }} />
              <Grid
                item
                xs={3}
                sx={{ borderRight: "1px solid #ccc", p: 1, fontSize: "1rem", fontWeight: "bold" }}
              >
                {data.totalExpenses.particulars}
              </Grid>
              <Grid
                item
                xs={2}
                sx={{
                  borderRight: "1px solid #ccc",
                  p: 1,
                  fontSize: "1rem",
                  fontWeight: "bold",
                  textAlign: "right",
                }}
              >
                {formatINR(data.totalExpenses.amount)}
              </Grid>
              <Grid item xs={6} />
            </Grid>
          )}
        </>
      )}

      {/* ─── MIS TABLE ─── */}
      {isIncomeByMis && (
        <>
          <Grid container sx={{ border: "1px solid #ccc" }}>
            <BusinessSummaryBar
              data={data}
              startDate={startDate}
              endDate={endDate}
              isIncomeByMis={isIncomeByMis}
              setAlert={setAlert}
              selectedDsaId={selectedDsaId}
            />

            {/* MIS Main Headers */}
            <Grid container>
              <Grid
                item
                xs={4}
                sx={{
                  borderRight: "1px solid #ccc",
                  p: 1,
                  background: "#e0f7fa",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                  Expenditure
                </Typography>
              </Grid>
              <Grid
                item
                xs={4}
                sx={{
                  borderRight: "1px solid #ccc",
                  p: 1,
                  background: "#e0f7fa",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                  Received Income
                </Typography>
              </Grid>
              <Grid
                item
                xs={4}
                sx={{
                  p: 1,
                  background: "#e0f7fa",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                  Not Received Income
                </Typography>
              </Grid>
            </Grid>

            {/* MIS Sub Headers */}
            {["Expenditure", "Received", "NotReceived"].map((type, idx) => (
              <React.Fragment key={type}>
                <Grid
                  item
                  xs={1}
                  sx={{
                    borderRight: "1px solid #ccc",
                    borderTop: "1px solid #ccc",
                    p: 1,
                    background: "#f5f5f5",
                    textAlign: "center",
                  }}
                >
                  <Typography variant="subtitle2">
                    <strong>S.No</strong>
                  </Typography>
                </Grid>
                <Grid
                  item
                  xs={2}
                  sx={{
                    borderRight: "1px solid #ccc",
                    borderTop: "1px solid #ccc",
                    p: 1,
                    background: "#f5f5f5",
                  }}
                >
                  <Typography variant="subtitle2">
                    <strong>Particulars</strong>
                  </Typography>
                </Grid>
                <Grid
                  item
                  xs={1}
                  sx={{
                    borderRight: idx === 2 ? "0px" : "1px solid #ccc",
                    borderTop: "1px solid #ccc",
                    p: 1,
                    background: "#f5f5f5",
                    textAlign: "right",
                  }}
                >
                  <Typography variant="subtitle2">
                    <strong>Amount</strong>
                  </Typography>
                </Grid>
              </React.Fragment>
            ))}
          </Grid>

          {/* MIS Table Body */}
          {(() => {
            const expRows = data.expenseDetails || [];
            const recRows = data.receivedIncome || [];
            const notRecRows = data.notReceivedIncome || [];
            const filteredMisRows = misSummaryRows.filter(({ key }) => data[key] != null);

            // FIX: include filteredMisRows.length in maxLength calculation
            const maxLength = Math.max(
              expRows.length,
              recRows.length + filteredMisRows.length,
              notRecRows.length
            );

            return Array.from({ length: maxLength }).map((_, idx) => (
              <Grid
                container
                key={idx}
                sx={{
                  borderLeft: "1px solid #ccc",
                  borderRight: "1px solid #ccc",
                  borderBottom: "1px solid #eee",
                  minHeight: "42px",
                }}
              >
                {/* Expenditure S.No */}
                <Grid
                  item
                  xs={1}
                  sx={{
                    borderRight: "1px solid #ccc",
                    p: 1,
                    textAlign: "center",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography sx={{ fontSize: "14px" }}>{expRows[idx] ? idx + 1 : ""}</Typography>
                </Grid>

                {/* Expenditure Particulars */}
                <Grid
                  item
                  xs={2}
                  sx={{
                    borderRight: "1px solid #ccc",
                    p: 1,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    sx={{ fontSize: "14px", display: "flex", alignItems: "center", width: "100%" }}
                  >
                    <span style={{ flex: 1, minWidth: 0, wordBreak: "break-word" }}>
                      {expRows[idx]?.particulars || ""}
                    </span>
                    {expRows[idx]?.particulars && (
                      <IconButton
                        size="small"
                        onClick={() => {
                          const p = expRows[idx].particulars.trim().toLowerCase();
                          if (p === "dma payouts") handleDmaPayoutsClick();
                          else if (p === "connector payouts") onConnectorClick();
                          else handleBreakdownClick(expRows[idx].particulars, "expense");
                        }}
                        sx={{
                          ml: 0.5,
                          flexShrink: 0,
                          color: "primary.main",
                          "&:hover": { backgroundColor: "rgba(255,152,0,0.1)" },
                        }}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Typography>
                </Grid>

                {/* Expenditure Amount */}
                <Grid
                  item
                  xs={1}
                  sx={{
                    borderRight: "1px solid #ccc",
                    p: 1,
                    textAlign: "right",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                  }}
                >
                  <Typography sx={{ fontSize: "14px", fontWeight: "bold" }}>
                    {expRows[idx]?.amount != null ? formatINR(expRows[idx].amount) : ""}
                  </Typography>
                </Grid>

                {/* Received Income side */}
                {(() => {
                  let recSerialNo = "";
                  let recParticulars = "";
                  let recAmount = null;
                  let recBold = false;
                  let recRed = false;
                  let recBg = "transparent";
                  let recTopBorder = false;
                  let recBrackets = false;
                  let isRegularRecRow = false;
                  let recOnIconClick = null;

                  if (idx < recRows.length) {
                    isRegularRecRow = true;
                    recSerialNo = idx + 1;
                    recParticulars = recRows[idx]?.particulars || "";
                    recAmount = recRows[idx]?.amount;
                  } else {
                    const misIdx = idx - recRows.length;
                    if (misIdx < filteredMisRows.length) {
                      const { key, label, bold, topBorder, bg, red, brackets, onIconClick } =
                        filteredMisRows[misIdx];
                      recParticulars = label || data[key]?.particulars || "";
                      recAmount = data[key]?.amount;
                      recBold = bold;
                      recRed = red;
                      recBg = bg;
                      recTopBorder = topBorder;
                      recBrackets = brackets;
                      recOnIconClick = onIconClick || null;
                    }
                  }

                  return (
                    <>
                      {/* Received S.No */}
                      <Grid
                        item
                        xs={1}
                        sx={{
                          borderRight: "1px solid #ccc",
                          p: 1,
                          textAlign: "center",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          background: recBg,
                          borderTop: recTopBorder ? "2px solid #aaa" : "none",
                        }}
                      >
                        <Typography sx={{ fontSize: "14px" }}>{recSerialNo}</Typography>
                      </Grid>

                      {/* Received Particulars */}
                      <Grid
                        item
                        xs={2}
                        sx={{
                          borderRight: "1px solid #ccc",
                          p: 1,
                          display: "flex",
                          alignItems: "center",
                          background: recBg,
                          borderTop: recTopBorder ? "2px solid #aaa" : "none",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: recBold ? "0.95rem" : "14px",
                            fontWeight: recBold ? "bold" : "normal",
                            color: recRed ? "red" : "inherit",
                            display: "flex",
                            alignItems: "center",
                            width: "100%",
                          }}
                        >
                          <span style={{ flex: 1, minWidth: 0, wordBreak: "break-word" }}>
                            {recParticulars}
                          </span>
                          {isRegularRecRow && recParticulars && (
                            <IconButton
                              size="small"
                              onClick={() => handleBreakdownClick(recParticulars, "misIncome")}
                              sx={{
                                ml: 0.5,
                                flexShrink: 0,
                                color: "primary.main",
                                "&:hover": { backgroundColor: "rgba(255,152,0,0.1)" },
                              }}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          )}
                          {!isRegularRecRow && recOnIconClick && recParticulars && (
                            <IconButton
                              size="small"
                              onClick={recOnIconClick}
                              sx={{
                                ml: 0.5,
                                flexShrink: 0,
                                color: "primary.main",
                                "&:hover": { backgroundColor: "rgba(33,150,243,0.1)" },
                              }}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Typography>
                      </Grid>

                      {/* Received Amount */}
                      <Grid
                        item
                        xs={1}
                        sx={{
                          borderRight: "1px solid #ccc",
                          p: 1,
                          textAlign: "right",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "flex-end",
                          background: recBg,
                          borderTop: recTopBorder ? "2px solid #aaa" : "none",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: recBold ? "0.95rem" : "14px",
                            fontWeight: recBold ? "bold" : "normal",
                            color: recRed ? "red" : "inherit",
                          }}
                        >
                          {recAmount != null
                            ? recBrackets
                              ? `(${formatINR(Math.abs(recAmount))})`
                              : formatINR(recAmount)
                            : ""}
                        </Typography>
                      </Grid>
                    </>
                  );
                })()}

                {/* Not Received S.No */}
                <Grid
                  item
                  xs={1}
                  sx={{
                    borderRight: "1px solid #ccc",
                    p: 1,
                    textAlign: "center",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography sx={{ fontSize: "14px" }}>
                    {notRecRows[idx] ? idx + 1 : ""}
                  </Typography>
                </Grid>

                {/* Not Received Particulars */}
                <Grid
                  item
                  xs={2}
                  sx={{
                    borderRight: "1px solid #ccc",
                    p: 1,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <Typography sx={{ fontSize: "14px" }}>
                    {notRecRows[idx]?.particulars || ""}
                  </Typography>
                </Grid>

                {/* Not Received Amount */}
                <Grid
                  item
                  xs={1}
                  sx={{
                    p: 1,
                    textAlign: "right",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                  }}
                >
                  <Typography sx={{ fontSize: "14px", fontWeight: "bold" }}>
                    {notRecRows[idx]?.amount != null ? formatINR(notRecRows[idx].amount) : ""}
                  </Typography>
                </Grid>
              </Grid>
            ));
          })()}

          {/* MIS Footer */}
          <Grid
            container
            sx={{ border: "1px solid #ccc", borderTop: "2px solid #F97316", background: "#f5f5f5" }}
          >
            <Grid item xs={1} sx={{ borderRight: "1px solid #ccc", p: 1 }} />
            <Grid
              item
              xs={2}
              sx={{ borderRight: "1px solid #ccc", p: 1, fontSize: "1rem", fontWeight: "bold" }}
            >
              Total Expense
            </Grid>
            <Grid
              item
              xs={1}
              sx={{
                borderRight: "1px solid #ccc",
                p: 1,
                fontSize: "1rem",
                textAlign: "right",
                fontWeight: "bold",
              }}
            >
              {data.finalExpenseTotal?.amount != null
                ? formatINR(data.finalExpenseTotal.amount)
                : ""}
            </Grid>
            <Grid item xs={1} sx={{ borderRight: "1px solid #ccc", p: 1 }} />
            <Grid
              item
              xs={2}
              sx={{ borderRight: "1px solid #ccc", p: 1, fontSize: "1rem", fontWeight: "bold" }}
            >
              Total Income
            </Grid>
            <Grid
              item
              xs={1}
              sx={{
                borderRight: "1px solid #ccc",
                p: 1,
                fontSize: "1rem",
                textAlign: "right",
                fontWeight: "bold",
              }}
            >
              {data.totalNetReceivedAmount?.amount != null
                ? formatINR(data.totalNetReceivedAmount.amount)
                : ""}
            </Grid>
            <Grid item xs={1} sx={{ borderRight: "1px solid #ccc", p: 1 }} />
            <Grid
              item
              xs={2}
              sx={{ borderRight: "1px solid #ccc", p: 1, fontSize: "1rem", fontWeight: "bold" }}
            >
              Not Received Total
            </Grid>
            <Grid
              item
              xs={1}
              sx={{ p: 1, fontSize: "1rem", textAlign: "right", fontWeight: "bold" }}
            >
              {data.notReceivedAmount?.amount != null
                ? formatINR(data.notReceivedAmount.amount)
                : ""}
            </Grid>
          </Grid>
        </>
      )}

      {/* Dialogs */}
      <ExpenseBreakdownDialog
        open={breakdownDialogOpen}
        onClose={handleCloseDialog}
        startDate={startDate}
        endDate={endDate}
        selectedLedger={selectedLedger}
        breakdownData={breakdownData}
        loading={loading}
        error={error}
        type={dialogType}
        selectedDsaId={selectedDsaId}
      />
      <DmaPayoutsDialog
        open={dmaPayoutDialogOpen}
        onClose={handleCloseDmaPayoutDialog}
        data={dmaPayoutData}
        loading={dmaPayoutLoading}
        error={dmaPayoutError}
        startDate={startDate}
        endDate={endDate}
      />
      <AdditionalPayoutsDialog
        open={additionalPayoutDialogOpen}
        onClose={() => setAdditionalPayoutDialogOpen(false)}
        startDate={startDate}
        endDate={endDate}
        selectedDsaId={selectedDsaId}
      />
      <RecoveriesDialog
        open={recoveriesDialogOpen}
        onClose={() => setRecoveriesDialogOpen(false)}
        startDate={startDate}
        endDate={endDate}
        selectedDsaId={selectedDsaId}
      />
    </Paper>
  );
}

ExpenditureIncomeReport.propTypes = {
  data: PropTypes.shape({
    income: PropTypes.number,
    expenseDetails: PropTypes.arrayOf(
      PropTypes.shape({
        month: PropTypes.string,
        particulars: PropTypes.string.isRequired,
        amount: PropTypes.number.isRequired,
      })
    ).isRequired,
    totalExpenses: PropTypes.shape({
      month: PropTypes.string,
      particulars: PropTypes.string.isRequired,
      amount: PropTypes.number.isRequired,
    }).isRequired,
    businessBreakdown: PropTypes.arrayOf(
      PropTypes.shape({
        month: PropTypes.string,
        particulars: PropTypes.string.isRequired,
        amount: PropTypes.number.isRequired,
      })
    ).isRequired,
    totalEstRev: PropTypes.shape({
      month: PropTypes.string,
      particulars: PropTypes.string,
      amount: PropTypes.number,
    }),
    receivedRevenue: PropTypes.shape({
      month: PropTypes.string,
      particulars: PropTypes.string,
      amount: PropTypes.number,
    }),
    estimatedNetProfit: PropTypes.shape({
      month: PropTypes.string,
      particulars: PropTypes.string,
      amount: PropTypes.number,
    }),
    subtotalGrossRevenue: PropTypes.shape({
      particulars: PropTypes.string,
      amount: PropTypes.number,
    }),
    debitNotes: PropTypes.shape({ particulars: PropTypes.string, amount: PropTypes.number }),
    creditNotes: PropTypes.shape({ particulars: PropTypes.string, amount: PropTypes.number }),
    netAdjustedRevenue: PropTypes.shape({
      particulars: PropTypes.string,
      amount: PropTypes.number,
    }),
    dmaPayouts: PropTypes.shape({ particulars: PropTypes.string, amount: PropTypes.number }),
    connectorPayouts: PropTypes.shape({ particulars: PropTypes.string, amount: PropTypes.number }),
    receivedIncome: PropTypes.arrayOf(
      PropTypes.shape({
        particulars: PropTypes.string.isRequired,
        amount: PropTypes.number.isRequired,
      })
    ),
    notReceivedIncome: PropTypes.arrayOf(
      PropTypes.shape({
        particulars: PropTypes.string.isRequired,
        amount: PropTypes.number.isRequired,
      })
    ),
    totalReceivedRevenue: PropTypes.shape({
      particulars: PropTypes.string,
      amount: PropTypes.number,
    }),
    notReceivedAmount: PropTypes.shape({ particulars: PropTypes.string, amount: PropTypes.number }),
    finalExpenseTotal: PropTypes.shape({ particulars: PropTypes.string, amount: PropTypes.number }),
    finalIncomeTotal: PropTypes.shape({ particulars: PropTypes.string, amount: PropTypes.number }),
    totalNetReceivedAmount: PropTypes.shape({
      particulars: PropTypes.string,
      amount: PropTypes.number,
    }),
  }).isRequired,
  startDate: PropTypes.string,
  endDate: PropTypes.string,
  isIncomeByMis: PropTypes.bool,
  setAlert: PropTypes.func,
  selectedDsaId: PropTypes.number,
  selectedBusinessId: PropTypes.number,
  count: PropTypes.number,
  page: PropTypes.number,
  rowsPerPage: PropTypes.number,
  handleChangePage: PropTypes.func,
  handleChangeRowsPerPage: PropTypes.func,
  onPageChange: PropTypes.func,
  onConnectorClick: PropTypes.func,
};
